import { CardContent } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import CardTitle from "@saleor/components/CardTitle";
import Skeleton from "@saleor/components/Skeleton";
import {
  Timeline,
  TimelineEvent,
  TimelineImage,
  TimelineNote
} from "@saleor/components/Timeline";
import TimelineNoteNationwide from "@saleor/components/Timeline/TimelineNoteNationwide";
import { isNationwideOrderEvents } from "@saleor/motherOrders/utils/handlers";
import {
  StoreOrderDetails_storeOrder_events,
  StoreOrderDetails_storeOrder_order_images
} from "@saleor/storeOrders/types/StoreOrderDetails";
import {
  DeliveryTypeEnum,
  OrderEventsEmailsEnum,
  OrderEventsEnum
} from "@saleor/types/globalTypes";
import lodash from "lodash";
import React from "react";
import { IntlShape, useIntl } from "react-intl";

interface StoreOrderHistoryProps {
  events: StoreOrderDetails_storeOrder_events[];
  images: StoreOrderDetails_storeOrder_order_images[];
  deliveryType: DeliveryTypeEnum;
}

const getEventMessage = (
  event: StoreOrderDetails_storeOrder_events,
  intl: IntlShape
) => {
  switch (event.type) {
    case OrderEventsEnum.CANCELED:
      return intl.formatMessage({
        defaultMessage: "Order was cancelled",
        description: "order history message"
      });
    case OrderEventsEnum.DRAFT_ADDED_PRODUCTS:
      return intl.formatMessage({
        defaultMessage: "Products were added to draft order",
        description: "order history message"
      });
    case OrderEventsEnum.DRAFT_CREATED:
      return intl.formatMessage({
        defaultMessage: "Draft order was created",
        description: "order history message"
      });
    case OrderEventsEnum.DRAFT_REMOVED_PRODUCTS:
      return intl.formatMessage({
        defaultMessage: "Products were deleted from draft order",
        description: "order history message"
      });
    case OrderEventsEnum.EMAIL_SENT:
      switch (event.emailType) {
        case OrderEventsEmailsEnum.DIGITAL_LINKS:
          return intl.formatMessage({
            defaultMessage: "Links to the order's digital goods were sent",
            description: "order history message"
          });
        case OrderEventsEmailsEnum.FULFILLMENT_CONFIRMATION:
          return intl.formatMessage({
            defaultMessage: "Fulfillment confirmation was sent to customer",
            description: "order history message"
          });
        case OrderEventsEmailsEnum.ORDER_CONFIRMATION:
          return intl.formatMessage({
            defaultMessage: "Order confirmation was sent to customer",
            description: "order history message"
          });
        case OrderEventsEmailsEnum.PAYMENT_CONFIRMATION:
          return intl.formatMessage({
            defaultMessage: "Payment confirmation was sent to customer",
            description: "order history message"
          });
        case OrderEventsEmailsEnum.SHIPPING_CONFIRMATION:
          return intl.formatMessage({
            defaultMessage: "Shipping details was sent to customer",
            description: "order history message"
          });
        case OrderEventsEmailsEnum.TRACKING_UPDATED:
          return intl.formatMessage({
            defaultMessage: "Shipping tracking number was sent to customer",
            description: "order history message"
          });
        case OrderEventsEmailsEnum.ORDER_CANCEL:
          return intl.formatMessage({
            defaultMessage: "Order cancel information was sent to customer",
            description: "order history message"
          });
        case OrderEventsEmailsEnum.ORDER_REFUND:
          return intl.formatMessage({
            defaultMessage: "Order refund information was sent to customer",
            description: "order history message"
          });
      }
    case OrderEventsEnum.FULFILLMENT_CANCELED:
      return intl.formatMessage({
        defaultMessage: "Fulfillment was cancelled",
        description: "order history message"
      });
    case OrderEventsEnum.INVOICE_REQUESTED:
      return intl.formatMessage(
        {
          defaultMessage: "Invoice was requested by {requestedBy}",
          description: "order history message"
        },
        {
          requestedBy: event.user ? event.user.email : null
        }
      );
    case OrderEventsEnum.INVOICE_GENERATED:
      return intl.formatMessage(
        {
          defaultMessage:
            "Invoice no. {invoiceNumber} was generated by {generatedBy}",
          description: "order history message"
        },
        {
          generatedBy: event.user ? event.user.email : null,
          invoiceNumber: event.invoiceNumber
        }
      );
    case OrderEventsEnum.INVOICE_UPDATED:
      return intl.formatMessage(
        {
          defaultMessage: "Invoice no. {invoiceNumber} was updated",
          description: "order history message"
        },
        {
          invoiceNumber: event.invoiceNumber
        }
      );
    case OrderEventsEnum.INVOICE_SENT:
      return intl.formatMessage(
        {
          defaultMessage: "Invoice was sent to customer by {sentBy}",
          description: "order history message"
        },
        {
          sentBy: event.user ? event.user.email : null
        }
      );
    case OrderEventsEnum.FULFILLMENT_FULFILLED_ITEMS:
      return intl.formatMessage(
        {
          defaultMessage: "Fulfilled {quantity} items",
          description: "order history message"
        },
        {
          quantity: event.quantity
        }
      );
    case OrderEventsEnum.FULFILLMENT_RESTOCKED_ITEMS:
      return intl.formatMessage(
        {
          defaultMessage: "Restocked {quantity} items",
          description: "order history message"
        },
        {
          quantity: event.quantity
        }
      );
    case OrderEventsEnum.NOTE_ADDED:
      return intl.formatMessage({
        defaultMessage: "Note was added to the order",
        description: "order history message"
      });
    case OrderEventsEnum.ORDER_FULLY_PAID:
      return intl.formatMessage({
        defaultMessage: "Order was fully paid",
        description: "order history message"
      });
    case OrderEventsEnum.ORDER_MARKED_AS_PAID:
      return intl.formatMessage({
        defaultMessage: "Marked order as paid",
        description: "order history message"
      });
    case OrderEventsEnum.OTHER:
      return event.message;
    case OrderEventsEnum.OVERSOLD_ITEMS:
      return intl.formatMessage(
        {
          defaultMessage: "Oversold {quantity} items",
          description: "order history message"
        },
        {
          quantity: event.quantity
        }
      );
    case OrderEventsEnum.PAYMENT_CAPTURED:
      return intl.formatMessage({
        defaultMessage: "Payment was captured",
        description: "order history message"
      });
    case OrderEventsEnum.PAYMENT_FAILED:
      return intl.formatMessage({
        defaultMessage: "Payment failed",
        description: "order history message"
      });
    case OrderEventsEnum.PAYMENT_REFUNDED:
      // return event.message;
      return intl.formatMessage({
        defaultMessage: "Payment was fully refunded",
        description: "order history message"
      });
    case OrderEventsEnum.PAYMENT_REFUND_REQUESTED:
      return intl.formatMessage({
        defaultMessage: "Payment refund was requested",
        description: "order history message"
      });
    case OrderEventsEnum.PAYMENT_FAILED_REFUND:
      // return event.message;
      return intl.formatMessage({
        defaultMessage: "Payment refund was failed.",
        description: "order history message"
      });
    case OrderEventsEnum.PAYMENT_MANUAL_REFUND_NOTE:
      return event.message;
    case OrderEventsEnum.PAYMENT_VOIDED:
      return intl.formatMessage({
        defaultMessage: "Payment was voided",
        description: "order history message"
      });
    case OrderEventsEnum.PLACED:
      return intl.formatMessage({
        defaultMessage: "Order was placed",
        description: "order history message"
      });
    case OrderEventsEnum.PLACED_FROM_DRAFT:
      return intl.formatMessage({
        defaultMessage: "Order was created from draft",
        description: "order history message"
      });
    case OrderEventsEnum.TRACKING_UPDATED:
      return intl.formatMessage({
        defaultMessage: "Updated fulfillment group's tracking number",
        description: "order history message"
      });
    case OrderEventsEnum.UPDATED_ADDRESS:
      return intl.formatMessage({
        defaultMessage: "Order address was updated",
        description: "order history message"
      });
    case OrderEventsEnum.PAYMENT_AUTHORIZED:
      return intl.formatMessage({
        defaultMessage: "Payment was authorized",
        description: "order history message"
      });
    case OrderEventsEnum.ORDER_MARKED_AS_PICKED_UP_BY_COURIER:
      return intl.formatMessage({
        defaultMessage: "Picked-Up by Courier",
        description: "order history message"
      });
    case OrderEventsEnum.ORDER_MARKED_AS_RECEIVED_BY_COURIER:
      return intl.formatMessage({
        defaultMessage: "Received by Courier",
        description: "order history message"
      });
    case OrderEventsEnum.ORDER_2GO_BOOKING_SUCCESS:
      return event.message;
    case OrderEventsEnum.ORDER_RETURNED_TO_FC:
      return event.message;
    case OrderEventsEnum.EXTERNAL_SERVICE_NOTIFICATION:
      return event.message;
    case OrderEventsEnum.ORDER_MARKED_AS_COMPLETED:
      return event.message;
    case OrderEventsEnum.ORDER_MARKED_AS_SHIPPED:
      return event.message;
  }
};

const StoreOrderHistory: React.FC<StoreOrderHistoryProps> = props => {
  const { events, images, deliveryType } = props;
  const intl = useIntl();
  const deliveryImage = lodash.last(images);

  const eventsWithMessage = [
    OrderEventsEnum.NOTE_ADDED,
    OrderEventsEnum.STORE_ORDER_PARCEL_NO_SET,
    OrderEventsEnum.STORE_ORDER_SCANNED_BY_RUNNER,
    OrderEventsEnum.STORE_ORDER_SCANNED_BY_OMI_OPS,
    OrderEventsEnum.STORE_ORDER_READY,
    OrderEventsEnum.STORE_ORDER_ACKNOWLEDGED,
    OrderEventsEnum.STORE_ORDER_RECEIVED_AT_FC,
    OrderEventsEnum.STORE_ORDER_CANCELED,
    OrderEventsEnum.STORE_ORDER_COMPLETED,
    OrderEventsEnum.ORDER_MARKED_AS_PICKED_UP_BY_COURIER,
    OrderEventsEnum.ORDER_MARKED_AS_RECEIVED_BY_COURIER,
    OrderEventsEnum.STORE_ORDER_PICK_LINE,
    OrderEventsEnum.STORE_ORDER_ADD_LINE,
    OrderEventsEnum.STORE_ORDER_COLLECTED,
    OrderEventsEnum.STORE_ORDER_DELETE_LINE,
    OrderEventsEnum.ORDER_MARKED_AS_COMPLETED,
    OrderEventsEnum.ORDER_MARKED_AS_PACKED,
    OrderEventsEnum.ORDER_MARKED_AS_PAID,
    OrderEventsEnum.ORDER_MARKED_AS_READY_TO_SHIP,
    OrderEventsEnum.ORDER_MARKED_AS_SHIPPED,
    OrderEventsEnum.ORDER_RETURNED_TO_FC,
    OrderEventsEnum.ORDER_ASSIGNED_TO_RIDER,
    OrderEventsEnum.ORDER_UPDATED_SHIPPING_LABEL,
    OrderEventsEnum.STORE_ORDER_OVERRIDE_ACKNOWLEDGED,
    OrderEventsEnum.STORE_ORDER_OVERRIDE_READY,
    OrderEventsEnum.ORDER_UPDATED_RIDER_DETAILS,
    OrderEventsEnum.STORE_ORDER_CANCEL_REVERT,
    OrderEventsEnum.RIDER_ARRIVED,
    OrderEventsEnum.STORE_ORDER_DROPPED_OFF_BY_RUNNER,
    OrderEventsEnum.STORE_ORDER_ASSIGNED_TO_RIDER,
    OrderEventsEnum.STORE_ORDER_OVERRIDDEN_BY_RUNNER,
    // OrderEventsEnum.PAYMENT_FAILED_REFUND,
    OrderEventsEnum.PAYMENT_MANUAL_REFUND_NOTE,
    // OrderEventsEnum.PAYMENT_REFUNDED
    OrderEventsEnum.ORDER_2GO_BOOKING_SUCCESS,
    OrderEventsEnum.REMITTANCE_MANUAL_OVERRIDE
  ];
  return (
    <Card>
      <CardTitle title="History" />
      <CardContent>
        <Timeline>
          {events ? (
            events
              .slice()
              .reverse()
              .map(event => {
                if (
                  deliveryType !== DeliveryTypeEnum.IN_STORE_PICKUP &&
                  event.type === OrderEventsEnum.ORDER_MARKED_AS_COMPLETED &&
                  deliveryImage?.url
                ) {
                  return (
                    <TimelineImage
                      date={event.date}
                      user={event.user}
                      message={event.message}
                      imageSrc={deliveryImage?.url}
                      key={event.id}
                    />
                  );
                } else if (
                  eventsWithMessage.includes(event.type) &&
                  isNationwideOrderEvents(deliveryType, event.type)
                ) {
                  return (
                    <TimelineNoteNationwide
                      date={event.date}
                      user={event.user}
                      email={event?.user?.email}
                      message={getEventMessage(event, intl)}
                      key={event.id}
                    />
                  );
                } else if (
                  eventsWithMessage.includes(event.type) &&
                  event.user !== null
                ) {
                  return (
                    <TimelineNote
                      date={event.date}
                      user={event.user}
                      message={event.message}
                      key={event.id}
                    />
                  );
                }
                return (
                  <TimelineEvent
                    date={event.date}
                    title={getEventMessage(event, intl)}
                    key={event.id}
                  />
                );
              })
          ) : (
            <Skeleton />
          )}
        </Timeline>
      </CardContent>
    </Card>
  );
};
StoreOrderHistory.displayName = "History";
export default StoreOrderHistory;
